/// <amd-module name="Wit/Common/BacklogContentService/BacklogContentTreeTypes" />
import { IObservableValue, ObservableValue } from "VSS/Core/Observable";
import { ITreeColumn } from "VSSUI/TreeEx";
import { ITreeItem, TreeItemProvider } from "VSSUI/Utilities/TreeItemProvider";
import { WorkItemTypeColorAndIcon } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { IFieldEntry } from "Wit/Common/Generated/Contracts";
import { WorkItemStateAndColor } from "Wit/UI/WorkItemState";
import { IBacklogColumn, IEffortData, RawRowData } from "./BacklogContentDataProviderTypes";

export interface IBacklogTree {
    allRows: { [id: number]: IBacklogTreeRow };
    rows: ITreeItem<IBacklogTreeRow>[];
    columnMap: { [columnId: string]: number };
    itemProvider: TreeItemProvider<IBacklogTreeRow>;
    visibleColumns: IBacklogColumn[];
    rollupColumns: IBacklogColumn[];
}

export interface IBacklogTreeRow extends IObservableValue<IBacklogItem> {
    children: IBacklogTreeRow[];
    id: number;
    parent: IBacklogTreeRow | undefined;
    iteration?: string;
    getFlattenedArray(validationMethod?: (workItem: IBacklogTreeRow) => Promise<boolean>, includeCurrentNode?: boolean): Promise<IBacklogTreeRow[]>;
}

export interface IBacklogItem {
    backlogLevel?: string;
    childWorkItemTypes: (WorkItemTypeColorAndIcon | undefined)[];
    columnValues: RawRowData[];
    coreColumnMap: { [columnName: string]: RawRowData };
    isValid: boolean;
    order?: number;
    workItemStateAndColor?: WorkItemStateAndColor;
    workItemTypeColorAndIcon?: WorkItemTypeColorAndIcon;
    workItemUrl: string;
    isIdentityResolved?: boolean;
}

export class ObservableBacklogItem extends ObservableValue<IBacklogItem> implements IBacklogTreeRow {
    public readonly children: IBacklogTreeRow[] = [];
    public readonly id: number;
    public parent: IBacklogTreeRow | undefined;
    constructor(id: number, parent: IBacklogTreeRow | undefined, value: IBacklogItem) {
        super(value);

        this.id = id;
        this.parent = parent;
    }

    /**
     * Flatten tree recursively and create linear structure containing all children
     */
    public async getFlattenedArray(validationMethod?: (workItem: IBacklogTreeRow) => Promise<boolean>, includeCurrentNode: boolean = true): Promise<IBacklogTreeRow[]> {
        const flatArray: IBacklogTreeRow[] = [];
        
        if (includeCurrentNode) {
            // If validation function is defined and not passed - return no result
            if (validationMethod && !(await validationMethod(this))) {
                return flatArray;
            }
            flatArray.push(this);
        }

        // Execute further depth flat only if children exist
        if (this.children?.length) {
            const promises = this.children.map(async (child) => child.getFlattenedArray(validationMethod));
            const results = await Promise.all(promises);
            results?.forEach((treeRow) => {
                flatArray.push(...treeRow);
            });
        }
        return flatArray;
    }
}

export interface IBacklogTreeColumn extends ITreeColumn<IBacklogTreeRow> {
    columnDetails: IBacklogColumn;
    columnIndex: number;
}

export interface IObservableForecastSettings {
    effortData: IEffortData;
    velocity: ObservableValue<number>;
    visibleState: string;
    inProgressStates: string[];
}

export const enum DisplayState {
    Loading,
    ZeroData,
    HasData
}

export const enum HubType {
    Backlogs = "Backlogs",
    Sprints = "Sprints"
}

export interface IBacklogFieldResponse {
    data?: IFieldEntry[];
    errorMessage?: string;
}
